import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { toZonedTime } from 'date-fns-tz';
import type { DetailedChangeset } from 'ember-changeset/types';
import GroupInvite from 'garaje/models/group-invite';
import type GroupInviteLink from 'garaje/models/group-invite-link';
import type RecurringGroupInvite from 'garaje/models/recurring-group-invite';
import { DEFAULT_TIMEZONE } from 'garaje/utils/date-fns-tz-utilities';
import { reads } from 'macro-decorators';
import { localCopy } from 'tracked-toolbox';

import GroupInviteLinkForm from './form';

type SupportedGroupInviteModels = GroupInvite | RecurringGroupInvite;
type SupportedGroupInviteChangesets = DetailedChangeset<SupportedGroupInviteModels>;
type SupportedGroupInvites = SupportedGroupInviteModels | SupportedGroupInviteChangesets;

type GroupInviteLinkPanelArgs = {
  enabled?: boolean;
  isEditing?: boolean;
  groupInvite?: SupportedGroupInvites;
  onGroupInviteLinkToggle?: (enabled: boolean) => void;
  timezone?: string;
};

interface GroupInviteLinkPanelSignature {
  Args: GroupInviteLinkPanelArgs;
}

const isGroupInvite = (
  groupInvite: SupportedGroupInvites | null | undefined,
): groupInvite is GroupInvite | DetailedChangeset<GroupInvite> => {
  if (!groupInvite) return false;

  // Check if it's a Changeset wrapping a GroupInvite
  if ('data' in groupInvite && groupInvite.data instanceof GroupInvite) return true;

  return groupInvite instanceof GroupInvite;
};

export default class GroupInviteLinkPanel extends Component<GroupInviteLinkPanelSignature> {
  @localCopy('args.enabled', false) isEnabled!: boolean;
  @reads('args.timezone', DEFAULT_TIMEZONE) timezone!: string;
  @reads('args.groupInvite.groupInviteLinks', []) _groupInviteLinks!: GroupInviteLink[];

  get groupInviteLinks(): GroupInviteLink[] {
    return isGroupInvite(this.args.groupInvite) ? this._groupInviteLinks : [];
  }

  get maxDate(): Date | null {
    const { groupInvite } = this.args;

    if (!isGroupInvite(groupInvite)) return null;

    const maxDate = groupInvite.expectedArrivalTime ?? new Date();

    return toZonedTime(maxDate, this.timezone);
  }

  get showAdditionalGuests(): boolean {
    const { groupInvite } = this.args;

    if (!isGroupInvite(groupInvite)) return false;

    return groupInvite.flow?.additionalGuests ?? false;
  }

  get showCheckbox(): boolean {
    return !this.args.isEditing;
  }

  get showGroupInviteLinks(): boolean {
    return this.isEnabled || !this.showCheckbox;
  }

  handleCapacityLimitChange = (groupInviteLink: GroupInviteLink, capacityLimit: number): void => {
    groupInviteLink.capacityLimit = capacityLimit;
  };

  handleAdditionalGuestsChange = (groupInviteLink: GroupInviteLink, additionalGuests: boolean): void => {
    groupInviteLink.additionalGuests = additionalGuests;
  };

  // If somehow the provided date is null, fallback to the maxDate or today/now
  handleExpiresAtChange = (groupInviteLink: GroupInviteLink, date: Date | null): void => {
    groupInviteLink.expiresAt = date ?? this.maxDate ?? toZonedTime(new Date(), this.timezone);
  };

  handleCheckboxChange = (checked: boolean): void => {
    this.isEnabled = checked;
    this.args.onGroupInviteLinkToggle?.(checked);
  };

  <template>
    {{#if this.groupInviteLinks.length}}
      <div class="mt-4 bg-carbon-5 rounded-md border border-carbon-10 p-6" data-test-registration-settings>
        {{#if this.showCheckbox}}
          <label
            class="flex items-center gap-3 cursor-pointer {{if this.isEnabled 'mb-6'}}"
            data-test-group-invite-link-checkbox
          >
            <input
              type="checkbox"
              checked={{this.isEnabled}}
              {{on "change" (pick "target.checked" this.handleCheckboxChange)}}
              class="size-3 rounded-sm border border-carbon-30 cursor-pointer"
              data-test-group-invite-link-checkbox
            />
            <span class="text-sm font-semibold text-md text-black">
              Create a signup link for self-registration
            </span>
          </label>
        {{/if}}

        {{#if this.showGroupInviteLinks}}
          <div data-test-group-invite-link-forms>
            {{#each this.groupInviteLinks as |groupInviteLink|}}
              <GroupInviteLinkForm
                @additionalGuests={{groupInviteLink.additionalGuests}}
                @capacityLimit={{groupInviteLink.capacityLimit}}
                @expiresAt={{groupInviteLink.expiresAt}}
                @maxDate={{this.maxDate}}
                @onAdditionalGuestsChange={{fn this.handleAdditionalGuestsChange groupInviteLink}}
                @onCapacityLimitChange={{fn this.handleCapacityLimitChange groupInviteLink}}
                @onExpiresAtChange={{fn this.handleExpiresAtChange groupInviteLink}}
                @publicUrl={{groupInviteLink.publicUrl}}
                @showAdditionalGuests={{this.showAdditionalGuests}}
                @timezone={{this.timezone}}
              />
            {{/each}}
          </div>
        {{/if}}
      </div>
    {{/if}}
  </template>
}
